import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Tooltip from "@material-ui/core/Tooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import * as api from "core-module/services/api";
import config from "config.js";
import "./PromoPackageSelection.css";

const money = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});
const speedNotice =
  "Speeds shown are advertised maximums. Actual speeds may vary with the installed radio technology, signal quality, the Ethernet link between your connection and router (such as 100 Mbps, 1 Gbps or 2.5 Gbps), and Wi-Fi signal strength and distance from the router.";
const newContractNotice =
  "At the end of your term, you may contact MCSnet to discuss a new contract. Any new contract is subject to the offers, eligibility and pricing available then and your agreement to its terms. A new contract or discount is not guaranteed.";

function ExtraHelp({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        arrow
        interactive
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        enterTouchDelay={0}
        leaveTouchDelay={10000}
        classes={{ tooltip: "promo-extra-tooltip" }}
        title={<div>{children}</div>}
      >
        <button
          type="button"
          className="promo-extra-help"
          aria-label={label}
          onClick={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
        >
          <HelpOutlineIcon fontSize="small" />
        </button>
      </Tooltip>
    </ClickAwayListener>
  );
}

export function selectionQuote(data, offerid, extraIds) {
  const offer = [...(data.offers || []), ...(data.monthtomonth || [])].find(
    (item) => String(item.id) === String(offerid)
  );
  const extras = (data.extras || []).filter((item) =>
    extraIds.includes(item.id)
  );
  if (!offer) return { offer: null, extras, monthly: null, installation: null };
  const amounts = [offer.monthly_total, offer.installation_total];
  extras.forEach((item) => amounts.push(item.monthly_total, item.setup_total));
  const valid =
    !data.unavailable_reason &&
    extraIds.every((id) => extras.some((item) => item.id === id)) &&
    amounts.every(
      (value) =>
        value !== null && value !== undefined && Number.isFinite(Number(value))
    );
  return {
    offer,
    extras,
    monthly: valid
      ? Number(offer.monthly_total) +
        extras.reduce((sum, item) => sum + Number(item.monthly_total), 0)
      : null,
    installation: valid
      ? Number(offer.installation_total) +
        extras.reduce((sum, item) => sum + Number(item.setup_total), 0)
      : null,
  };
}

function CancellationTerms({ offer }) {
  const maximum = Number(offer.cancellation_max || 0);
  const monthly = Number(offer.cancellation_per_month || 0);
  const flat = Number(offer.cancellation_flat || 0);
  if (maximum <= 0 || (monthly === 0 && flat === 0)) {
    return <>There is no early cancellation fee.</>;
  }
  return (
    <>
      Early cancellation costs {money.format(monthly)} per remaining month
      {flat !== 0 && (
        <>
          {flat > 0 ? " plus a " : " minus a "}
          {money.format(Math.abs(flat))}
          {flat > 0 ? " flat fee" : " credit"}
        </>
      )}
      . The combined charge is capped at {money.format(maximum)}.
    </>
  );
}

function contractDuration(months) {
  const term = Number(months);
  return term >= 12 && term % 12 === 0
    ? `${term / 12} ${term === 12 ? "year" : "years"} (${term} months)`
    : `${term} ${term === 1 ? "month" : "months"}`;
}

function RenewalPrice({ offer }) {
  const price = offer.renewal_monthly_total;
  const term = Number(offer.termmonths);
  const duration =
    term >= 12 && term % 12 === 0
      ? `${term / 12} ${term === 12 ? "year" : "years"}`
      : `${term} ${term === 1 ? "month" : "months"}`;
  const available =
    price !== null &&
    price !== undefined &&
    price !== "" &&
    Number.isFinite(Number(price)) &&
    Number(price) >= 0;
  return (
    <p className="promo-renewal-price">
      {available ? (
        <>
          Then <strong>{money.format(Number(price))}/month</strong> after the{" "}
          {duration}
        </>
      ) : (
        "Renewal price unavailable"
      )}
    </p>
  );
}

function RenewalTerms({ offer, contractname }) {
  return (
    <>
      <RenewalPrice offer={offer} />
      <p>
        Service continues month to month under{" "}
        {contractname || "the renewal agreement"}. Current renewal prices are
        before tax, exclude extras and are not a future price guarantee.
      </p>
      <p>{newContractNotice}</p>
    </>
  );
}

function OfferCard({
  offer,
  selected,
  onSelect,
  disabled,
  compact,
  renewalContractname,
}) {
  const adjustment = Number(offer.discount_monthly || 0);
  const term = Number(offer.termmonths);
  const inputId = "internet-offer-" + offer.id;
  const eligible = Array.isArray(offer.eligiblepackages)
    ? offer.eligiblepackages
    : [];
  return (
    <article
      className={
        "promo-plan " +
        (selected ? "promo-plan-selected " : "") +
        (compact ? "promo-plan-flexible" : "")
      }
    >
      <label htmlFor={inputId} className="promo-plan-choice">
        <div className="promo-plan-heading">
          <div>
            <span className="promo-eyebrow">
              {term === 24
                ? "2-year agreement"
                : term > 0
                ? `${term}-month agreement`
                : "Month to month"}
            </span>
            <h3>{offer.name}</h3>
          </div>
          <input
            id={inputId}
            type="radio"
            name="internet-offer"
            value={offer.id}
            checked={selected}
            onChange={() => onSelect(offer.id)}
            disabled={disabled}
            aria-label={
              offer.name +
              ", " +
              offer.contractname +
              ", " +
              money.format(Number(offer.monthly_total)) +
              " per month"
            }
          />
        </div>
        <div className="promo-plan-price">
          <strong>{money.format(Number(offer.monthly_total))}</strong>
          <span>/ month</span>
        </div>
        {term > 0 && (
          <p className="promo-price-duration">
            For {term === 24 ? "2 years" : contractDuration(term)}.
          </p>
        )}
        {adjustment < 0 ? (
          <p className="promo-price-note">
            <span className="promo-base-price">
              {money.format(Number(offer.base_monthly))}
            </span>{" "}
            <span className="promo-savings-pill">
              Save {money.format(Math.abs(adjustment))}/month
            </span>
          </p>
        ) : adjustment > 0 ? (
          <p className="promo-price-note">
            Base price {money.format(Number(offer.base_monthly))} plus{" "}
            {money.format(adjustment)} monthly contract fee
          </p>
        ) : (
          <p className="promo-price-note">
            {term > 0 ? "Before tax" : "No fixed-term commitment"}
          </p>
        )}
        {term > 0 && (
          <div className="promo-card-terms">
            <RenewalPrice offer={offer} />
          </div>
        )}
        <ul className="promo-features">
          {Number(offer.downloadmbps) > 0 && (
            <li className="promo-feature-primary">
              <span>Download</span>{" "}
              <strong>
                Up to{" "}
                {(Number(offer.downloadmbps) === 1000
                  ? 940
                  : Number(offer.downloadmbps)
                ).toLocaleString("en-CA")}{" "}
                Mbps
              </strong>
            </li>
          )}
          {Number(offer.uploadmbps) > 0 && (
            <li>
              <span>Upload</span>{" "}
              <strong>
                Up to{" "}
                {(Number(offer.uploadmbps) === 1000
                  ? 940
                  : Number(offer.uploadmbps)
                ).toLocaleString("en-CA")}{" "}
                Mbps
              </strong>
            </li>
          )}
          <li className="promo-feature-primary">
            <span>Monthly data</span>{" "}
            <strong>
              {offer.capgb === null
                ? "Unlimited"
                : Number(offer.capgb).toLocaleString("en-CA") + " GB"}
            </strong>
          </li>
          <li>
            <span>Installation</span>{" "}
            <strong>
              {Number(offer.installation_total) === 0
                ? "Free"
                : money.format(Number(offer.installation_total))}
            </strong>
          </li>
          {(Number(offer.downloadmbps) > 0 || Number(offer.uploadmbps) > 0) && (
            <li className="promo-speed-note">Advertised maximum speeds</li>
          )}
        </ul>
        <span className="promo-select-label">
          {selected ? "Selected" : "Choose this plan"}
          {selected && <CheckCircleOutlineIcon fontSize="small" />}
        </span>
      </label>
      <details className="promo-plan-details">
        <summary>Plan details &amp; conditions</summary>
        <div>
          {term > 0 && (
            <>
              <p>
                Commit to {contractDuration(term)}.{" "}
                <CancellationTerms offer={offer} />
              </p>
              <RenewalTerms offer={offer} contractname={renewalContractname} />
            </>
          )}
          {offer.needautopay && (
            <p>Automatic payments are required for this plan.</p>
          )}
          <p>{speedNotice}</p>
          <p>Prices are in Canadian dollars, before tax.</p>
          {eligible.length > 0 ? (
            <>
              <p>
                Future plan changes stay within this agreement. Its available
                packages are:
              </p>
              <ul>
                {eligible.map((item) => (
                  <li key={item.ratebanddetailid || item.packageid}>
                    {item.name} — {money.format(Number(item.monthly_total))}
                    /month
                  </li>
                ))}
              </ul>
              {eligible.length === 1 && (
                <p>
                  This agreement has one package. A lower-priced package is not
                  available within it.
                </p>
              )}
            </>
          ) : (
            <p>
              Future plan changes are limited to the packages included in this
              agreement.
            </p>
          )}
        </div>
      </details>
    </article>
  );
}

function ReferralCredit({ referral }) {
  if (referral?.status === "unavailable" && referral.message)
    return (
      <p className="promo-code-message" role="status">
        {referral.message}
      </p>
    );
  const credit = Number(referral?.first_bill_credit);
  if (referral?.status !== "applied" || !Number.isFinite(credit) || credit <= 0)
    return null;

  return (
    <section className="promo-referral-credit" aria-label="Referral credit">
      <h3>Referral credit</h3>
      {referral.displayname && <p>Referred by {referral.displayname}</p>}
      <strong>{money.format(credit)} credit added on your first bill</strong>
      <p className="promo-referral-note">
        A one-time credit, separate from your monthly rate and installation
        price.
      </p>
    </section>
  );
}

export default function PromoPackageSelection({
  initialData,
  userid,
  history,
}) {
  const initialCode = String(
    (initialData.promocode !== undefined
      ? initialData.promocode
      : initialData.selection?.promocode) || ""
  )
    .trim()
    .toUpperCase();
  const [data, setData] = useState(initialData);
  const [offerid, setOfferid] = useState(initialData.selection?.offerid || "");
  const [extraIds, setExtraIds] = useState(
    Array.isArray(initialData.selection?.extras)
      ? initialData.selection.extras
      : []
  );
  const [promocode, setPromocode] = useState(initialCode);
  const [appliedCode, setAppliedCode] = useState(initialCode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [document, setDocument] = useState(null);
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const documentUrlRef = useRef("");
  const documentRequestRef = useRef(0);
  const extrasSectionRef = useRef(null);
  const extraNamesRef = useRef(
    Object.fromEntries(
      (initialData.extras || []).map((item) => [item.id, item.name])
    )
  );
  const actionInFlight = useRef(false);
  const mounted = useRef(true);
  const endpoint = "user." + userid + "/preinstallpackage";
  const quote = selectionQuote(data, offerid, extraIds);
  const unavailableExtraIds = extraIds.filter(
    (id) => !(data.extras || []).some((item) => item.id === id)
  );
  const revision = data.revision ?? data.selection?.revision ?? null;
  const savedExtras = Array.isArray(data.selection?.extras)
    ? data.selection.extras
    : [];
  const changed =
    data.selectionstale === true ||
    String(data.selection?.offerid || "") !== String(offerid) ||
    JSON.stringify([...savedExtras].sort()) !==
      JSON.stringify([...extraIds].sort()) ||
    String(data.selection?.promocode || "")
      .trim()
      .toUpperCase() !== appliedCode;
  const accepted =
    data.selectionstale !== true &&
    data.agreement?.status === "accepted" &&
    String(data.agreement?.revision) === String(revision);
  const unappliedCode = promocode.trim().toUpperCase() !== appliedCode;
  const available =
    quote.offer &&
    quote.monthly !== null &&
    quote.installation !== null &&
    !data.unavailable_reason &&
    unavailableExtraIds.length === 0 &&
    !unappliedCode;

  useEffect(
    () => () => {
      mounted.current = false;
      if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
    },
    []
  );

  async function refreshOffers(event) {
    if (event) event.preventDefault();
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const response = await api.get(endpoint, {
        httpGetParameters: {
          promocode: encodeURIComponent(promocode.trim().toUpperCase()),
        },
      });
      api.requireApiSuccess(
        response,
        "Unable to refresh your available offers."
      );
      const refreshed = response.object || response.list?.[0] || response;
      if (!mounted.current) return;
      const currentCode = String(
        (refreshed.promocode !== undefined ? refreshed.promocode : promocode) ||
          ""
      )
        .trim()
        .toUpperCase();
      setData(refreshed);
      setPromocode(currentCode);
      setAppliedCode(currentCode);
      (refreshed.extras || []).forEach((item) => {
        extraNamesRef.current[item.id] = item.name;
      });
      const choices = [
        ...(refreshed.offers || []),
        ...(refreshed.monthtomonth || []),
      ];
      const previousPlanUnavailable =
        offerid && !choices.some((item) => String(item.id) === String(offerid));
      if (previousPlanUnavailable) setOfferid("");
      setReviewOpen(false);
      setAgreed(false);
      setNotice(
        previousPlanUnavailable
          ? "Your previous plan is not available with these offers. Choose a new plan and review its current prices before saving."
          : currentCode
          ? "Offers updated for your code. Review the current prices before saving."
          : "Your current available offers are shown below."
      );
    } catch (e) {
      if (mounted.current)
        setError(
          e.message ||
            "Unable to refresh your available offers. Your choices are still here."
        );
    } finally {
      actionInFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  async function saveSelection() {
    if (actionInFlight.current || !available || !agreed) return;
    actionInFlight.current = true;
    setBusy(true);
    setError("");
    let savedRevision = revision;
    try {
      if (changed || !savedRevision) {
        const response = await api.post(endpoint, {
          offerid,
          extras: extraIds,
          promocode: appliedCode,
          quoteversion: data.quoteversion,
          expectedrevision: revision,
        });
        api.requireApiSuccess(response, "Unable to save your selection.");
        let refreshed =
          response.result || response.object || response.list?.[0];
        if (!refreshed || !refreshed.mode) {
          const reload = await api.get(endpoint);
          api.requireApiSuccess(
            reload,
            "Your selection was saved, but its confirmation could not be loaded. Refresh offers to check it."
          );
          refreshed = reload.object || reload.list?.[0] || reload;
        }
        if (!mounted.current) return;
        setData(refreshed);
        const savedCode = String(
          (refreshed.promocode !== undefined
            ? refreshed.promocode
            : refreshed.selection?.promocode ?? appliedCode) || ""
        )
          .trim()
          .toUpperCase();
        setPromocode(savedCode);
        setAppliedCode(savedCode);
        savedRevision =
          refreshed.revision ??
          refreshed.selection?.revision ??
          response.revision;
        if (!savedRevision)
          throw new Error(
            "Your selection was saved, but its revision could not be confirmed. Refresh offers before continuing."
          );
      }
      const confirmation = await api.post(endpoint, {
        action: "accept",
        revision: savedRevision,
        accepted: true,
      });
      api.requireApiSuccess(
        confirmation,
        "Your selection is saved. We could not record your agreement yet; please try confirming it again."
      );
      if (!mounted.current) return;
      const refreshed =
        confirmation.result || confirmation.object || confirmation.list?.[0];
      if (refreshed?.mode) {
        setData(refreshed);
        const savedCode = String(
          (refreshed.promocode !== undefined
            ? refreshed.promocode
            : refreshed.selection?.promocode ?? appliedCode) || ""
        )
          .trim()
          .toUpperCase();
        setPromocode(savedCode);
        setAppliedCode(savedCode);
      } else
        setData((current) => ({
          ...current,
          agreement: {
            ...(current.agreement || {}),
            status: "accepted",
            revision: savedRevision,
          },
        }));
      setReviewOpen(false);
      setAgreed(false);
      setNotice(
        "Your selection and agreement are saved. Your disclosure is available below. Continue with your installation checklist when you're ready."
      );
    } catch (e) {
      if (mounted.current)
        setError(
          e.message ||
            "Unable to save your selection. Your choices have been kept. Refresh offers if prices or availability have changed."
        );
    } finally {
      actionInFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  async function viewDisclosure(item) {
    const request = ++documentRequestRef.current;
    setDocument(item);
    setDocumentUrl("");
    setDocumentError("");
    setDocumentLoading(true);
    if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
    try {
      if (!item.downloadtarget)
        throw new Error(
          "This disclosure is not ready to download. Please refresh offers and try again."
        );
      const response = await api.fetchApi(
        "?target=" + encodeURIComponent(item.downloadtarget)
      );
      if (!response?.ok)
        throw new Error("Unable to open your disclosure. Please try again.");
      const blob = await response.blob();
      if (!blob.type.includes("pdf"))
        throw new Error(
          "Your disclosure could not be retrieved. Please try again."
        );
      if (!mounted.current || request !== documentRequestRef.current) return;
      const url = URL.createObjectURL(blob);
      documentUrlRef.current = url;
      setDocumentUrl(url);
    } catch (e) {
      if (mounted.current && request === documentRequestRef.current)
        setDocumentError(e.message || "Unable to open your disclosure.");
    } finally {
      if (mounted.current && request === documentRequestRef.current)
        setDocumentLoading(false);
    }
  }

  function closeDisclosure() {
    documentRequestRef.current += 1;
    setDocument(null);
    setDocumentUrl("");
    if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
    documentUrlRef.current = "";
  }

  function chooseOffer(id) {
    setOfferid(id);
    setNotice("");
    const reducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (extrasSectionRef.current?.scrollIntoView) {
      extrasSectionRef.current.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
    }
  }

  function chooseExtra(id, checked) {
    setExtraIds((current) => {
      const remaining = current.filter(
        (item) =>
          item !== id &&
          (!checked ||
            !["static2", "static5"].includes(id) ||
            !["static2", "static5"].includes(item))
      );
      return checked ? [...remaining, id] : remaining;
    });
    setNotice("");
  }

  return (
    <div className="promo-selection">
      <Link
        className="promo-back"
        to={config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"}
      >
        ← Installation checklist
      </Link>
      <header className="promo-intro">
        <span className="promo-eyebrow">Internet that fits your life</span>
        <h1>Choose your internet plan</h1>
        <p>
          Find your fit, add what you need, and see the full price before you
          agree.
        </p>
      </header>
      {error && (
        <div className="promo-message promo-error" role="alert">
          <strong>We couldn't complete that step.</strong>
          <p>{error}</p>
          <Button onClick={refreshOffers} disabled={busy}>
            Refresh offers
          </Button>
        </div>
      )}
      {notice && (
        <div className="promo-message promo-success" role="status">
          {notice}
        </div>
      )}
      {data.selectionstale === true && (
        <div className="promo-message promo-info" role="status">
          <strong>Your saved selection needs a fresh review.</strong>
          <p>
            Prices or available offers have changed. Your choices are kept
            below; review the current details and save a new disclosure before
            continuing.
          </p>
        </div>
      )}
      {data.unavailable_reason && (
        <div className="promo-message promo-error" role="alert">
          <strong>Your available offers could not be confirmed.</strong>
          <p>{data.unavailable_reason}</p>
          <Button onClick={refreshOffers} disabled={busy}>
            Try again
          </Button>
        </div>
      )}
      {unavailableExtraIds.length > 0 && (
        <div className="promo-message promo-error" role="alert">
          <strong>Some selected extras are no longer available.</strong>
          <p>
            Your choices have been kept. Refresh offers or remove these extras
            before reviewing your updated total:
          </p>
          <ul>
            {unavailableExtraIds.map((id) => (
              <li key={id}>{extraNamesRef.current[id] || id}</li>
            ))}
          </ul>
          <Button
            disabled={busy}
            onClick={() => {
              setExtraIds((ids) =>
                ids.filter((id) => !unavailableExtraIds.includes(id))
              );
              setAgreed(false);
              setNotice(
                "Unavailable extras removed. Review your updated total before saving."
              );
            }}
          >
            Remove unavailable extras
          </Button>
          <Button onClick={refreshOffers} disabled={busy}>
            Refresh offers
          </Button>
        </div>
      )}
      <div className="promo-layout">
        <div className="promo-sidebar">
          <aside
            className="promo-summary"
            aria-labelledby="promo-summary-heading"
          >
            <span className="promo-eyebrow">No surprises</span>
            <h2 id="promo-summary-heading">Your selection</h2>
            {quote.offer ? (
              <>
                <p className="promo-summary-plan">
                  <strong>{quote.offer.name}</strong>
                  <span>
                    {Number(quote.offer.termmonths) > 0
                      ? "For " + contractDuration(quote.offer.termmonths) + "."
                      : "Month to month"}
                  </span>
                </p>
                <dl className="promo-cost-lines">
                  <div>
                    <dt>Internet</dt>
                    <dd>{money.format(Number(quote.offer.monthly_total))}</dd>
                  </div>
                  {quote.extras.map((extra) => (
                    <div key={extra.id}>
                      <dt>{extra.name}</dt>
                      <dd>{money.format(Number(extra.monthly_total))}</dd>
                    </div>
                  ))}
                </dl>
                <div className="promo-total" aria-live="polite">
                  <span>Monthly total</span>
                  <strong>
                    {quote.monthly !== null
                      ? money.format(quote.monthly)
                      : "Unavailable"}
                    <small> / month</small>
                  </strong>
                  <span>Before tax</span>
                </div>
                {Number(quote.offer.termmonths) > 0 && (
                  <RenewalPrice offer={quote.offer} />
                )}
                <div className="promo-installation">
                  <span>Installation &amp; setup</span>
                  <strong>
                    {quote.installation !== null
                      ? money.format(quote.installation)
                      : "Unavailable"}
                  </strong>
                </div>
                {quote.offer.needautopay && (
                  <p className="promo-summary-condition">
                    Automatic payments required
                  </p>
                )}
                {Number(quote.offer.termmonths) > 0 && (
                  <>
                    <p className="promo-summary-condition">
                      <CancellationTerms offer={quote.offer} /> Review the full
                      conditions before saving.
                    </p>
                    <p className="promo-summary-condition">
                      {newContractNotice}
                    </p>
                  </>
                )}
              </>
            ) : (
              <p className="promo-summary-empty">
                Choose a plan to see your monthly total and installation price
                here.
              </p>
            )}
            <ReferralCredit referral={data.referral} />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!available || busy}
              onClick={() => {
                setAgreed(false);
                setReviewOpen(true);
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {!changed && revision && !accepted
                ? "Confirm your selection"
                : "Review selection"}
            </Button>
            {unappliedCode && (
              <p className="promo-summary-condition">Apply your code first.</p>
            )}
            {!changed && accepted && available && (
              <Button
                className="promo-checklist-button"
                fullWidth
                onClick={() =>
                  history.push(
                    config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"
                  )
                }
              >
                Continue to checklist
              </Button>
            )}
            <p className="promo-summary-footer">
              You'll review the prices and conditions before your selection is
              saved.
            </p>
          </aside>
          <section
            className="promo-code-box"
            aria-labelledby="promo-code-heading"
          >
            <h3 id="promo-code-heading">Have a promo or referral code?</h3>
            <form onSubmit={refreshOffers}>
              <TextField
                id="promo-code-input"
                label="Promo or referral code"
                variant="outlined"
                size="small"
                value={promocode}
                onChange={(event) => setPromocode(event.target.value)}
                disabled={busy}
                inputProps={{
                  maxLength: 80,
                  "aria-describedby": "promo-code-help",
                }}
              />
              <Button type="submit" variant="outlined" disabled={busy}>
                {busy ? "Updating…" : "Apply code"}
              </Button>
            </form>
            <p id="promo-code-help" className="promo-muted">
              Enter a promo code or a code shared by an MCSnet customer.
              {data.referral?.locked &&
                " Your existing referral is kept when you apply a promo code."}
            </p>
            {unappliedCode && (
              <p className="promo-muted">
                Apply your code to refresh prices before reviewing your
                selection.
              </p>
            )}
          </section>
        </div>
        <main className="promo-options">
          {(data.offers || []).length > 0 && (
            <section aria-labelledby="promo-featured-heading">
              <div className="promo-section-heading">
                <span className="promo-step">1</span>
                <div>
                  <h2 id="promo-featured-heading">Save with a contract</h2>
                  <p>Choose a plan with a lower monthly price.</p>
                </div>
              </div>
              <div
                className="promo-plan-grid"
                role="radiogroup"
                aria-labelledby="promo-featured-heading"
              >
                {data.offers.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    renewalContractname={data.tier?.renew_contractname}
                    selected={String(offerid) === String(offer.id)}
                    onSelect={chooseOffer}
                    disabled={busy}
                  />
                ))}
              </div>
            </section>
          )}
          {(data.monthtomonth || []).length > 0 && (
            <details
              className="promo-flexible-section"
              aria-labelledby="promo-flexible-heading"
            >
              <summary>
                <h2 id="promo-flexible-heading">Prefer month to month?</h2>
                <span className="promo-flexible-action">
                  <span className="promo-flexible-show">View plans</span>
                  <span className="promo-flexible-hide">Hide plans</span>
                </span>
              </summary>
              <p className="promo-muted">
                Choose flexibility with no term discount. Installation charges
                are shown for each plan.
              </p>
              <div
                className="promo-plan-grid"
                role="radiogroup"
                aria-labelledby="promo-flexible-heading"
              >
                {data.monthtomonth.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    renewalContractname={data.tier?.renew_contractname}
                    selected={String(offerid) === String(offer.id)}
                    onSelect={chooseOffer}
                    disabled={busy}
                    compact
                  />
                ))}
              </div>
            </details>
          )}
          {(data.extras || []).length > 0 && (
            <section
              ref={extrasSectionRef}
              className="promo-extras-section"
              aria-labelledby="promo-extras-heading"
            >
              <div className="promo-section-heading">
                <span className="promo-step">2</span>
                <div>
                  <h2 id="promo-extras-heading">Make it yours</h2>
                  <p>Optional extras. You can choose one static IP option.</p>
                </div>
              </div>
              <div className="promo-extras-grid">
                {data.extras.map((extra) => (
                  <div
                    key={extra.id}
                    className={
                      "promo-extra " +
                      (extra.id === "router" ||
                      extra.id === "phone" ||
                      extra.id === "static2" ||
                      extra.id === "static5"
                        ? "promo-extra-has-help "
                        : "") +
                      (extraIds.includes(extra.id)
                        ? "promo-extra-selected"
                        : "")
                    }
                  >
                    <label className="promo-extra-choice">
                      <input
                        type="checkbox"
                        checked={extraIds.includes(extra.id)}
                        onChange={(event) =>
                          chooseExtra(extra.id, event.target.checked)
                        }
                        disabled={busy}
                      />
                      <span>
                        <strong>{extra.name}</strong>
                        <span className="promo-extra-description">
                          {extra.description ||
                            (extra.id === "router"
                              ? "MCSnet-managed Wi-Fi for your home."
                              : extra.id === "phone"
                              ? "Add a home phone service."
                              : "Dedicated public IP addresses for your connection.")}
                        </span>
                        <span className="promo-extra-price">
                          {money.format(Number(extra.monthly_total))}
                          <span> / month</span>
                          {Number(extra.setup_total) !== 0 && (
                            <small>
                              {" "}
                              + {money.format(Number(extra.setup_total))} setup
                            </small>
                          )}
                        </span>
                        {(extra.id === "router" ||
                          (Number(extra.termmonths || 0) <= 1 &&
                            Number(extra.cancellation_max || 0) === 0 &&
                            Number(extra.cancellation_per_month || 0) === 0 &&
                            Number(extra.cancellation_flat || 0) === 0)) && (
                          <span className="promo-extra-flexibility">
                            Month to month - cancel anytime
                          </span>
                        )}
                        {extra.id === "router" && (
                          <span className="promo-extra-description">
                            Return the equipment when you cancel. A{" "}
                            {money.format(Number(extra.cancellation_flat))} fee
                            applies if it is not returned.
                          </span>
                        )}
                      </span>
                    </label>
                    {extra.id === "router" && (
                      <ExtraHelp label="About MCSnet Router">
                        <strong>Wi-Fi made simple</strong>
                        <p>
                          You’re welcome to use your own router. MCSnet Router
                          is an optional rental with automatic setup, so you
                          don’t need to configure it yourself.
                        </p>
                        <p>
                          Change your Wi-Fi name and password directly in
                          MyAccount, or let our team set them up for you. It’s
                          an easy way to leave the technical setup to us.
                        </p>
                      </ExtraHelp>
                    )}
                    {extra.id === "phone" && (
                      <ExtraHelp label="About MCSnet Home Phone">
                        <strong>Your home phone, made simple</strong>
                        <p>
                          MCSnet Home Phone is a VoIP “landline” that makes
                          calls over your Internet connection. We provide a
                          small adapter that connects your Internet service to
                          your home’s phone line. Plug it in and use your home
                          phones as usual.
                        </p>
                        <p>
                          <strong>Bring your phone number with you.</strong> We
                          can transfer your existing number from your old
                          provider. We’ll contact them on your behalf and help
                          you through the process.
                        </p>
                        <p>
                          <strong>
                            Don’t cancel your old phone service yet.
                          </strong>{" "}
                          Keep it active until the number transfer is complete.
                        </p>
                      </ExtraHelp>
                    )}
                    {(extra.id === "static2" || extra.id === "static5") && (
                      <ExtraHelp label="About static IP addresses">
                        <strong>Do I need a static IP?</strong>
                        <p>
                          Every Internet package already includes a working
                          dynamic IP address. It can change over time and is
                          suitable for everyday browsing, streaming and gaming.
                        </p>
                        <p>
                          A static IP address stays the same. It can be useful
                          for remotely accessing equipment, hosting services, or
                          a VPN or business network that needs a fixed address.
                        </p>
                        <p>
                          Most homes don’t need one. This is an optional add-on.
                        </p>
                      </ExtraHelp>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
          <p className="promo-fineprint">
            All prices are in Canadian dollars before tax. Speeds are advertised
            maximums and actual speeds may vary. Your service address determines
            which offers are available.
          </p>
          {(data.disclosures || []).length > 0 && (
            <section
              className="promo-records"
              aria-labelledby="promo-records-heading"
            >
              <div className="promo-section-heading">
                <DescriptionOutlinedIcon />
                <div>
                  <h2 id="promo-records-heading">Your selection records</h2>
                  <p>
                    Each saved change has its own disclosure. Earlier records
                    stay available.
                  </p>
                </div>
              </div>
              <ul>
                {data.disclosures.map((item) => (
                  <li key={item.documentid || item.revision}>
                    <div>
                      <strong>Selection {item.revision}</strong>
                      {item.created_at && (
                        <span>
                          {new Date(item.created_at).toLocaleString("en-CA", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </span>
                      )}
                      {String(item.revision) === String(revision) && (
                        <span className="promo-record-state">
                          {accepted
                            ? "Current · agreed"
                            : "Current · agreement needed"}
                        </span>
                      )}
                    </div>
                    <Button size="small" onClick={() => viewDisclosure(item)}>
                      View disclosure
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </main>
      </div>
      <Dialog
        open={reviewOpen}
        onClose={() => {
          if (!busy) setReviewOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="promo-review-title"
      >
        <DialogTitle id="promo-review-title">Review your selection</DialogTitle>
        <DialogContent className="promo-review">
          {quote.offer && (
            <>
              <h3>{quote.offer.name}</h3>
              <p>
                {quote.offer.contractname} ·{" "}
                {Number(quote.offer.termmonths) > 0
                  ? "For " + contractDuration(quote.offer.termmonths) + "."
                  : "Month to month"}
              </p>
              <dl className="promo-cost-lines">
                <div>
                  <dt>Internet base price</dt>
                  <dd>{money.format(Number(quote.offer.base_monthly))}</dd>
                </div>
                {Number(quote.offer.discount_monthly) !== 0 && (
                  <div>
                    <dt>
                      Monthly contract{" "}
                      {Number(quote.offer.discount_monthly) < 0
                        ? "discount"
                        : "fee"}
                    </dt>
                    <dd>
                      {Number(quote.offer.discount_monthly) < 0 ? "−" : "+"}
                      {money.format(
                        Math.abs(Number(quote.offer.discount_monthly))
                      )}
                    </dd>
                  </div>
                )}
                {quote.extras.map((extra) => (
                  <div key={extra.id}>
                    <dt>{extra.name}</dt>
                    <dd>{money.format(Number(extra.monthly_total))}</dd>
                  </div>
                ))}
                <div className="promo-review-total">
                  <dt>Monthly total before tax</dt>
                  <dd>
                    {quote.monthly !== null
                      ? money.format(quote.monthly)
                      : "Unavailable"}
                  </dd>
                </div>
                <div>
                  <dt>Installation &amp; setup before tax</dt>
                  <dd>
                    {quote.installation !== null
                      ? money.format(quote.installation)
                      : "Unavailable"}
                  </dd>
                </div>
              </dl>
              <ReferralCredit referral={data.referral} />
              <div className="promo-review-conditions">
                <h4>Your agreement</h4>
                <ul>
                  {Number(quote.offer.termmonths) > 0 ? (
                    <>
                      <li>
                        This agreement lasts{" "}
                        {contractDuration(quote.offer.termmonths)}.
                        {Number(quote.offer.discount_monthly) !== 0 && (
                          <>
                            {" "}
                            The monthly contract{" "}
                            {Number(quote.offer.discount_monthly) < 0
                              ? "discount"
                              : "fee"}{" "}
                            applies during that term.
                          </>
                        )}
                      </li>
                      <li>
                        <CancellationTerms offer={quote.offer} />
                      </li>
                      <li>
                        <RenewalTerms
                          offer={quote.offer}
                          contractname={data.tier?.renew_contractname}
                        />
                      </li>
                    </>
                  ) : (
                    <li>
                      Month-to-month service has no fixed-term commitment.
                    </li>
                  )}
                  {quote.offer.needautopay && (
                    <li>Automatic payments are required.</li>
                  )}
                  <li>
                    Plan changes remain within the packages included in this
                    agreement.
                    {quote.offer.eligiblepackages?.length === 1
                      ? " This agreement does not offer a lower-priced package."
                      : ""}
                  </li>
                  <li>{speedNotice}</li>
                  <li>Prices are in Canadian dollars before tax.</li>
                </ul>
              </div>
              {quote.extras.length > 0 && (
                <div className="promo-review-conditions promo-review-extras">
                  <h4>Your extras</h4>
                  <p>
                    These services have their own agreement conditions, separate
                    from your Internet plan.
                  </p>
                  {quote.extras.map((extra) => (
                    <section key={extra.id} data-extra-id={extra.id}>
                      <h5>{extra.name}</h5>
                      <p>
                        {extra.contractname || extra.name} —{" "}
                        {extra.id === "router"
                          ? "Month to month - cancel anytime."
                          : Number(extra.termmonths) > 1
                          ? `${extra.termmonths}-month agreement.`
                          : Number(extra.cancellation_max || 0) === 0 &&
                            Number(extra.cancellation_per_month || 0) === 0 &&
                            Number(extra.cancellation_flat || 0) === 0
                          ? "Month-to-month service. Cancel anytime."
                          : "Month-to-month service with no fixed-term commitment."}
                      </p>
                      <p>
                        {extra.id === "router" ? (
                          <>
                            Return the equipment when you cancel. A{" "}
                            {money.format(Number(extra.cancellation_flat))} fee
                            applies if it is not returned.
                          </>
                        ) : (
                          <CancellationTerms offer={extra} />
                        )}
                      </p>
                      {extra.needautopay && (
                        <p>Automatic payments are required for this extra.</p>
                      )}
                    </section>
                  ))}
                </div>
              )}
              <label className="promo-consent">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                  disabled={busy}
                />
                <span>
                  I have reviewed my plan, extras, prices and contract
                  conditions above and agree to this selection.
                </span>
              </label>
              <p className="promo-fineprint">
                Saving creates a disclosure for this selection and records your
                agreement. Your subscriber agreement is completed separately in
                the installation checklist.
              </p>
            </>
          )}
          {error && (
            <div className="promo-message promo-error" role="alert">
              {error}
              <Button
                disabled={busy}
                onClick={() => {
                  setReviewOpen(false);
                  refreshOffers();
                }}
              >
                Refresh offers
              </Button>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)} disabled={busy}>
            Back to plans
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!agreed || !available || busy}
            onClick={saveSelection}
          >
            {busy ? "Saving…" : "Agree and save selection"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(document)}
        onClose={closeDisclosure}
        maxWidth="md"
        fullWidth
        aria-labelledby="promo-disclosure-title"
      >
        <DialogTitle id="promo-disclosure-title">
          Selection disclosure {document?.revision}
        </DialogTitle>
        <DialogContent>
          {documentLoading && <p role="status">Opening your disclosure…</p>}
          {documentError && (
            <div className="promo-message promo-error" role="alert">
              {documentError}
              <Button onClick={() => viewDisclosure(document)}>
                Try again
              </Button>
            </div>
          )}
          {documentUrl && (
            <iframe
              title="Your MCSnet selection disclosure"
              className="promo-pdf"
              src={documentUrl}
            />
          )}
        </DialogContent>
        <DialogActions>
          {documentUrl && (
            <Button
              component="a"
              href={documentUrl}
              download={document?.filename || "MCSnet-selection.pdf"}
            >
              Download PDF
            </Button>
          )}
          <Button onClick={closeDisclosure}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
